/**
 * Edit Class Page
 * Edit existing class information
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { apiRequest } from '@/api/client';

interface ClassFormData {
  className: string;
  gradeLevel: string;
  ageRating: string;
}

const ageOptions = [
  { 
    label: 'Toddlers (2-4)', 
    value: 'toddlers',
    colourBands: ['pink', 'red']
  },
  { 
    label: 'Children (5-8)', 
    value: 'children',
    colourBands: ['yellow', 'blue', 'green', 'orange', 'turquoise', 'purple']
  },
  { 
    label: 'Young Teens (9-12)', 
    value: 'young_teens',
    colourBands: ['gold', 'white', 'lime']
  },
  { 
    label: 'Teens (13-17)', 
    value: 'teens',
    colourBands: []
  }
];

const colourBands: Record<string, { color: string; level: number; ageRange: string; description: string }> = {
  pink: { color: '#FFC0CB', level: 1, ageRange: '4-5', description: 'Simplest - repetitive text, 20-50 words' },
  red: { color: '#FF6B6B', level: 2, ageRange: '4-5', description: 'Building confidence - 50-100 words' },
  yellow: { color: '#FFD93D', level: 3, ageRange: '5-6', description: 'Varied patterns - 100-150 words' },
  blue: { color: '#6BCF7F', level: 4, ageRange: '5-6', description: 'Longer stories - 150-250 words' },
  green: { color: '#4CAF50', level: 5, ageRange: '6-7', description: 'Building stamina - 250-400 words' },
  orange: { color: '#FF9800', level: 6, ageRange: '6-7', description: 'Independent reading - 400-600 words' },
  turquoise: { color: '#40E0D0', level: 7, ageRange: '7-8', description: 'Confident readers - 600-800 words' },
  purple: { color: '#9C27B0', level: 8, ageRange: '7-8', description: 'Fluent readers - 800-1000 words' },
  gold: { color: '#FFD700', level: 9, ageRange: '8-9', description: 'Advanced readers - 1000-1500 words' },
  white: { color: '#E0E0E0', level: 10, ageRange: '9-10', description: 'Highly competent - 1500-2000 words' },
  lime: { color: '#CDDC39', level: 11, ageRange: '10+', description: 'Expert readers - 2000-3000 words' }
};

export const EditClassPage = () => {
  const { classId } = useParams<{ classId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  const [formData, setFormData] = useState<ClassFormData>({
    className: '',
    gradeLevel: '',
    ageRating: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<ClassFormData>>({});
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  useEffect(() => {
    fetchClass();
  }, [classId]);

  const fetchClass = async () => {
    try {
      setIsLoading(true);
      
      // Fetch classes - use get_school_classes for admins, get_teacher_classes for teachers
      const action = user?.isAdmin ? 'get_school_classes' : 'get_teacher_classes';
      const params = user?.isAdmin 
        ? { schoolId: user?.schoolId }
        : { userEmail: user?.email };
      
      const response = await apiRequest<{ classes: any[] }>(action, params);

      if (response.success && (response as any).classes) {
        const classData = (response as any).classes.find((c: any) => c.classId === classId);
        
        if (classData) {
          setFormData({
            className: classData.className || '',
            gradeLevel: classData.gradeLevel || '',
            ageRating: classData.settings?.ageRating || classData.ageRating || 'children',
          });
        } else {
          showNotification('error', 'Class not found');
          navigate('/classes');
        }
      }
    } catch (error) {
      console.error('Failed to fetch class:', error);
      showNotification('error', 'Failed to load class');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof ClassFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const toggleCategory = (categoryValue: string) => {
    setExpandedCategory(expandedCategory === categoryValue ? null : categoryValue);
  };

  const validate = (): boolean => {
    const newErrors: Partial<ClassFormData> = {};

    if (!formData.className.trim()) {
      newErrors.className = 'Class name is required';
    }

    if (!formData.gradeLevel) {
      newErrors.gradeLevel = 'Grade level is required';
    }

    if (!formData.ageRating) {
      newErrors.ageRating = 'Age rating is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await apiRequest('update_class', {
        userEmail: user?.email,
        classId: classId,
        className: formData.className,
        gradeLevel: formData.gradeLevel,
        settings: {
          ageRating: formData.ageRating,
        },
      });

      if (response.success) {
        showNotification('success', 'Class updated successfully');
        navigate(`/classes/${classId}`);
      } else {
        showNotification('error', response.error || 'Failed to update class');
      }
    } catch (error) {
      console.error('Failed to update class:', error);
      showNotification('error', 'Failed to update class');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Edit Class</h1>
        <p className="text-slate-600 mt-2">Update class information</p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Class Name */}
          <div>
            <label htmlFor="className" className="block text-sm font-medium text-slate-700 mb-2">
              Class Name *
            </label>
            <input
              type="text"
              id="className"
              value={formData.className}
              onChange={(e) => handleChange('className', e.target.value)}
              className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                errors.className ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="e.g., Year 5 English"
            />
            {errors.className && (
              <p className="text-red-600 text-sm mt-1">{errors.className}</p>
            )}
          </div>

          {/* Grade Level */}
          <div>
            <label htmlFor="gradeLevel" className="block text-sm font-medium text-slate-700 mb-2">
              Year Level *
            </label>
            <input
              type="text"
              id="gradeLevel"
              value={formData.gradeLevel}
              onChange={(e) => handleChange('gradeLevel', e.target.value)}
              className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                errors.gradeLevel ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="e.g., 5"
            />
            {errors.gradeLevel && (
              <p className="text-red-600 text-sm mt-1">{errors.gradeLevel}</p>
            )}
          </div>

          {/* Age Rating / Colour Bands */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Reading Level *
            </label>
            <p className="text-sm text-slate-500 mb-3">
              Select an age group or expand to see Oxford Reading Tree colour bands
            </p>
            
            {ageOptions.map((rating) => (
              <div key={rating.value} className="mb-3">
                {/* Main age rating row */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleChange('ageRating', rating.value)}
                    className={`flex-1 px-4 py-3 border rounded-xl text-left transition-colors ${
                      formData.ageRating === rating.value
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                        : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                    }`}
                  >
                    <div className="font-medium">{rating.label}</div>
                  </button>
                  
                  {/* Expand button for colour bands */}
                  {rating.colourBands && rating.colourBands.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleCategory(rating.value)}
                      className="px-4 py-3 border border-slate-300 rounded-xl bg-white hover:bg-slate-50 transition-colors"
                    >
                      <div className="text-center">
                        <div className="text-lg">{expandedCategory === rating.value ? '▼' : '▶'}</div>
                        <div className="text-xs text-slate-600">Bands</div>
                      </div>
                    </button>
                  )}
                </div>

                {/* Expanded colour bands */}
                {expandedCategory === rating.value && rating.colourBands && rating.colourBands.length > 0 && (
                  <div className="mt-3 ml-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <p className="text-sm font-medium text-slate-700 mb-3">
                      Oxford Reading Tree Colour Bands:
                    </p>
                    <div className="space-y-2">
                      {rating.colourBands.map((bandName) => {
                        const band = colourBands[bandName];
                        return (
                          <button
                            key={bandName}
                            type="button"
                            onClick={() => handleChange('ageRating', bandName)}
                            className={`w-full flex items-center gap-3 px-4 py-3 border rounded-lg text-left transition-colors ${
                              formData.ageRating === bandName
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                            }`}
                          >
                            <div
                              className="w-6 h-6 rounded-full border-2 border-slate-300"
                              style={{ backgroundColor: band.color }}
                            />
                            <div className="flex-1">
                              <div className="font-medium text-slate-900">
                                {bandName.charAt(0).toUpperCase() + bandName.slice(1)} Band
                              </div>
                              <div className="text-sm text-slate-600">
                                Level {band.level} • Ages {band.ageRange} • {band.description}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {errors.ageRating && (
              <p className="text-red-600 text-sm mt-1">{errors.ageRating}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Updating...' : 'Update Class'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/classes/${classId}`)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
